import { RefObject } from 'react'

import { useToast } from '@chakra-ui/react'
import {
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query'

import {
  ChangeTranscriptLanguageRequest,
  GetInterviewResponse,
} from '@pkg/types/api'

import api from '@/api'
import { INTERVIEW_KEY, useInterview } from '@/react-query/interview/interview'

import { uploadFileWithRetry } from './upload-file'

export const useCancelTranscription = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async () => {
      if (!interviewId) {
        return
      }
      await api.cancelTranscription(interviewId)
    },
    onMutate: () => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (oldData?: GetInterviewResponse) => ({
          ...oldData,
          isFullTranscriptGenerating: false,
          detectedTranscriptLanguage: null,
        }),
      )
    },
  })
}

export const CHANGE_TRANSCRIPT_LANGUAGE_KEY =
  'change-transcript-language-mutation'

export const useChangeTranscriptLanguage = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationKey: [CHANGE_TRANSCRIPT_LANGUAGE_KEY, interviewId],
    mutationFn: async (data: ChangeTranscriptLanguageRequest) => {
      if (!interviewId) {
        return
      }
      await api.changeTranscriptLanguage(interviewId, data)

      return {
        isFullTranscriptGenerating: true,
        setTranscriptLanguage: data.language,
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (oldData?: GetInterviewResponse) => ({
          ...oldData,
          ...data,
        }),
      )
    },
  })
}

// apparently useMutation.isPending state is not shared across different components,
// that's why we need useChangeTranscriptLanguageIsPending.
export const useChangeTranscriptLanguageIsPending = () => {
  const interviewId = useInterview().data?.id

  const mutationState = useMutationState({
    filters: {
      mutationKey: [CHANGE_TRANSCRIPT_LANGUAGE_KEY, interviewId],
    },
  })

  const lastMutationStatus =
    mutationState?.[mutationState.length - 1 || 0]?.status
  return lastMutationStatus === 'pending'
}

export const useUploadAndStartTranscription = (
  setFileUploadProgress: (progress: number) => void,
  setIsFileUploading: (isUploading: boolean) => void,
  uploadAbortController: RefObject<AbortController | null>,
) => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id
  const toast = useToast()

  return useMutation({
    mutationFn: async ({
      file,
      interviewId,
      source,
    }: {
      file: File
      interviewId: string
      source: 'localUpload' | 'micRecording' | 'tabRecording'
    }) => {
      if (!interviewId || !uploadAbortController.current) {
        return
      }

      setIsFileUploading(true)
      setFileUploadProgress(0)

      const { uploadUrl, gcsObjectName } = (
        await api.generateResumableUploadUrl({
          fileName: file.name,
          interviewId,
        })
      ).data

      console.log('Uploading file to GCS', {
        uploadUrl,
        gcsObjectName,
      })
      if (!uploadAbortController.current) {
        return
      }
      await uploadFileWithRetry(
        file,
        uploadUrl,
        uploadAbortController.current.signal,
        (progress) => setFileUploadProgress(progress),
        3, // maxRetries
      )

      setFileUploadProgress(100)
      setIsFileUploading(false)

      console.log('Starting transcription from GCS', {
        interviewId,
        gcsObjectName,
        source,
      })
      await api.startTranscriptionFromGCS({
        interviewId,
        gcsObjectName,
        source,
      })
    },
    onSuccess: () => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (oldData?: GetInterviewResponse) => ({
          ...oldData,
          isFullTranscriptGenerating: true,
        }),
      )
      setFileUploadProgress(0)
      setIsFileUploading(false)
    },
    onError: (error: unknown) => {
      setIsFileUploading(false)
      setFileUploadProgress(0)
      queryClient.invalidateQueries({
        queryKey: [INTERVIEW_KEY, interviewId],
      })

      console.error('Error uploading file:', error)

      const errorMessage =
        error instanceof Error ? error.message : 'Please try again'

      toast({
        title: 'Error uploading file',
        description: errorMessage,
        status: 'error',
      })
    },
  })
}
